import { useState } from "react";
import { toast } from "sonner";
import { Save, Info } from "lucide-react";
import { useContentDoc, useResource, SectionHeader, Loading, Field, PrimaryBtn, ImagePicker, inputCls } from "./components";

export default function HomepageSection() {
  const { content, setContent, save } = useContentDoc();
  const news = useResource("news");
  const [saving, setSaving] = useState(false);

  if (!content || news.loading) return <Loading />;

  const set = (k) => (e) => setContent({ ...content, [k]: e.target.value });

  const saveAll = async () => {
    setSaving(true);
    try {
      await save(content);
      toast.success("Homepage saved! Changes are live.");
    } catch {
      toast.error("Could not save. Please try again.");
    }
    setSaving(false);
  };

  return (
    <div data-testid="admin-homepage-section">
      <SectionHeader title="Homepage" subtitle="Control the first thing visitors see: the big photo, the headline, and the featured announcement." />

      <div className="space-y-6">
        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <h2 className="font-condensed text-2xl font-bold uppercase text-[#0F4C2A] mb-5">Hero (Top of Homepage)</h2>
          <div className="space-y-4">
            <ImagePicker label="Hero Image" value={content.hero_image} onChange={(url) => setContent({ ...content, hero_image: url })} testid="homepage-hero-image" />
            <Field label="Small line above the title">
              <input className={inputCls} value={content.hero_kicker || ""} onChange={set("hero_kicker")} data-testid="homepage-kicker" />
            </Field>
            <Field label="Big title" hint="Press Enter to split onto two lines">
              <textarea rows={2} className={inputCls} value={content.hero_title || ""} onChange={set("hero_title")} data-testid="homepage-title" />
            </Field>
            <Field label="Description under the title">
              <textarea rows={3} className={inputCls} value={content.hero_subtitle || ""} onChange={set("hero_subtitle")} data-testid="homepage-subtitle" />
            </Field>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <h2 className="font-condensed text-2xl font-bold uppercase text-[#0F4C2A] mb-5">Featured Announcement</h2>
          <Field label="Pin one announcement to the top of the homepage news" hint="Choose 'None' to simply show the newest announcements">
            <select className={inputCls} value={content.featured_news_id || ""} onChange={set("featured_news_id")} data-testid="homepage-featured-news">
              <option value="">None — show newest first</option>
              {news.items.map((n) => (
                <option key={n.id} value={n.id}>{n.title}</option>
              ))}
            </select>
          </Field>
        </div>

        <div className="flex items-start gap-3 bg-[#0F4C2A]/5 border border-[#0F4C2A]/20 rounded-md px-4 py-4">
          <Info size={18} className="text-[#0F4C2A] shrink-0 mt-0.5" />
          <p className="text-sm text-slate-700">The homepage automatically pulls in your <strong>schedule, record, announcements, and photos</strong> — update those in their own sections and the homepage updates by itself.</p>
        </div>
      </div>

      <div className="sticky bottom-4 mt-8 flex justify-end">
        <PrimaryBtn onClick={saveAll} disabled={saving} className="shadow-xl px-8 py-4 text-base" data-testid="homepage-save-button">
          <Save size={17} /> {saving ? "Saving..." : "Save Homepage"}
        </PrimaryBtn>
      </div>
    </div>
  );
}
